import React, { useState, useMemo, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/modern/components/ui/dialog-shadcn';
import { Button } from '@/modern/components/ui/button';
import { Checkbox } from '@/modern/components/ui/checkbox';
import { ScrollArea } from '@/modern/components/ui/scroll-area';
import { GRANTS } from '@/other/constants/PrivilegeTypes';
import { copyEntityToOtherBoard } from '@/network/network';
import { pluralize } from '@/other/utils/otherUtils';
import { cn } from '@/modern/utils';
import { toast } from 'sonner';
import BryntumContext from '@/react/context/bryntumContext';

interface CopyToBoardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  record: any;
  entityType: string;
  entityName?: string;
}

export const CopyToBoardDialog: React.FC<CopyToBoardDialogProps> = ({
  open,
  onOpenChange,
  record,
  entityType,
  entityName,
}) => {
  const { boardManager, selectedBoard } = useContext(BryntumContext);
  const [selectedBoardIds, setSelectedBoardIds] = useState<Set<number | string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);

  // Reset selection when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedBoardIds(new Set());
    }
  }, [open]);

  // Get boards with copy access (grant_four)
  const availableBoards = useMemo(() => {
    //@ts-expect-error boardManager is not typed in BryntumContext.
    if (!boardManager?.boardNameStore?.records) return [];

    //@ts-expect-error boardManager is not typed in BryntumContext.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return boardManager.boardNameStore.records.filter(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (board: any) =>
        board.id !== selectedBoard?.id &&
        board.grants?.includes(GRANTS.grant_four)
    );
  }, [boardManager]);

  const handleToggleBoard = (boardId: number | string) => {
    setSelectedBoardIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(boardId)) {
        newSet.delete(boardId);
      } else {
        newSet.add(boardId);
      }
      return newSet;
    });
  };

  const handleToggleAll = () => {
    if (selectedBoardIds.size === availableBoards.length) {
      setSelectedBoardIds(new Set());
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setSelectedBoardIds(new Set(availableBoards.map((board: any) => board.id)));
    }
  };

  const handleCopy = async () => {
    if (selectedBoardIds.size === 0) return;

    setIsLoading(true);
    try {
      await Promise.all(
        Array.from(selectedBoardIds).map((boardId) =>
          copyEntityToOtherBoard(entityType, record.id, boardId)
        )
      );

      toast.success(
        `The entity has been copied to the selected ${pluralize(
          selectedBoardIds.size,
          'board',
          's'
        )}`
      );

      onOpenChange(false);
      setSelectedBoardIds(new Set());
    } catch (error) {
      toast.error('Sorry, something went wrong. Unable to copy!');
    } finally {
      setIsLoading(false);
    }
  };

  const displayName = entityName || record?.name || 'this entity';

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent className="sm:max-w-[600px]" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="text-xl">
            Copy to boards
          </DialogTitle>
          <DialogDescription className="truncate max-w-[20rem]">
            Select the boards you want to copy <span className="font-semibold">"{displayName}"</span> to.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {availableBoards.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <i className="fa-light fa-inbox fa-3x mb-4 block opacity-20" />
              <p className="text-base font-medium">No boards available</p>
              <p className="text-sm mt-1">You need edit access to other boards.</p>
            </div>
          ) : (
            <>
              {/* Select All Checkbox */}
              <div
                className="flex items-center space-x-3 p-4 border rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={handleToggleAll}
              >
                <Checkbox
                  id="select-all"
                  checked={selectedBoardIds.size === availableBoards.length}
                  onClick={(e) => e.stopPropagation()}
                  className="pointer-events-none"
                />
                <div className="text-sm font-semibold leading-none flex-1">
                  Select All ({availableBoards.length} {pluralize(availableBoards.length, 'board', 's')})
                </div>
              </div>

              {/* Board List */}
              <ScrollArea className="h-[350px] pr-3">
                <div className="space-y-2">
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  {availableBoards.map((board: any) => (
                    <div
                      key={board.id}
                      className={cn(
                        'flex items-center space-x-3 p-4 rounded-lg hover:bg-accent cursor-pointer transition-all border',
                        selectedBoardIds.has(board.id) ? 'bg-accent border-primary' : 'border-border'
                      )}
                      onClick={() => handleToggleBoard(board.id)}
                    >
                      <Checkbox
                        id={`board-${board.id}`}
                        checked={selectedBoardIds.has(board.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="pointer-events-none"
                      />
                      <div className="flex-1 text-sm font-medium leading-snug">
                        {board.name}
                      </div>
                      {selectedBoardIds.has(board.id) && (
                        <i className="fa-solid fa-check text-primary" />
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleCopy}
            disabled={selectedBoardIds.size === 0 || isLoading}
            className="min-w-[140px]"
          >
            {isLoading ? (
              <>
                <i className="fa-light fa-spinner fa-spin mr-2" />
                Copying...
              </>
            ) : (
              <>
                <i className="fa-light fa-copy mr-2" />
                Copy to {selectedBoardIds.size} {pluralize(selectedBoardIds.size, 'board', 's')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};